import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ClipboardList, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { showSaveSuccess } from '@/lib/swal'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { employeesService } from '@/services/employeesService'
import { purchaseRequisitionService } from '@/services/purchaseRequisitionService'

import type { DepartmentLookup } from '@/api/types'
import type { PurchaseRequisitionLine, PurchaseRequisitionMaster } from '@/services/purchaseRequisitionService'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function newLine(): PurchaseRequisitionLine {
  return { id: 0, itemId: 0, quantity: 0, isDeleted: 'false' }
}

export const PurchaseRequisitionEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const numericId = Number(id || 0)
  const isEditMode = !isNew && Number.isFinite(numericId) && numericId > 0
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId || 0
  const userId = user?.userId || 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [itemOptions, setItemOptions] = useState<{ id: number; name: string }[]>([])

  const [master, setMaster] = useState<PurchaseRequisitionMaster>({
    id: 0,
    code: '',
    date: todayIso(),
    reference: '',
    departmentId: 0,
    estimatedDeliveryDate: todayIso(),
    status: 0,
    attachmentPath: '',
    branchId,
    createdDate: undefined,
    createdBy: undefined,
    updatedDate: undefined,
    updatedById: undefined,
    isEditable: 0,
    attachmentFile: null,
  })

  const [lines, setLines] = useState<PurchaseRequisitionLine[]>(Array.from({ length: 3 }, () => newLine()))

  const addRows = (count: number) => setLines((prev) => [...prev, ...Array.from({ length: count }, () => newLine())])

  const removeLine = (idx: number) => {
    setLines((prev) => prev.filter((_, i) => i !== idx))
  }

  const updateLine = (idx: number, patch: Partial<PurchaseRequisitionLine>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
  }

  useEffect(() => {
    setMaster((p) => ({ ...p, branchId }))
  }, [branchId])

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [deps, items] = await Promise.all([employeesService.getDepartmentLookups(), purchaseRequisitionService.getItems(branchId)])
        setDepartments(deps || [])
        setItemOptions(items || [])
      } catch {
        setDepartments([])
        setItemOptions([])
      }
    }
    if (!branchId) return
    void loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!branchId || !isEditMode) return
    const load = async () => {
      try {
        setLoading(true)
        const [m, l] = await Promise.all([
          purchaseRequisitionService.getById(numericId, branchId),
          purchaseRequisitionService.getLinesByRequisitionId(numericId, branchId),
        ])
        setMaster({
          ...m,
          branchId,
          attachmentFile: null,
        })
        setLines(l.length ? l : Array.from({ length: 3 }, () => newLine()))
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('common.failed'))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, isEditMode, numericId, t])

  const departmentOptions = useMemo(() => {
    return (departments || []).map((d) => ({
      value: String(d.id),
      label: language === 'ar' ? d.lookupNameAr || d.lookupNameEn : d.lookupNameEn || d.lookupNameAr,
    }))
  }, [departments, language])

  const itemSelectOptions = useMemo(() => {
    return (itemOptions || []).map((x) => ({ value: String(x.id), label: x.name }))
  }, [itemOptions])

  const handleSave = async () => {
    if (!branchId) {
      toast.error(t('common.selectBranchFirst'))
      return
    }
    if (!master.departmentId) {
      toast.error(t('purchaseRequisitions.validation.requiredFields'))
      return
    }

    const validLines = lines
      .map((l) => ({
        ...l,
        itemId: Number(l.itemId || 0),
        quantity: Number(l.quantity || 0),
        isDeleted: l.isDeleted || 'false',
      }))
      .filter((l) => l.itemId > 0 && l.quantity > 0)

    if (validLines.length === 0) {
      toast.error(t('purchaseRequisitions.validation.atLeastOneItem'))
      return
    }

    try {
      setSaving(true)
      const payload = {
        ...master,
        id: isEditMode ? master.id : 0,
        branchId,
        createdDate: master.createdDate || new Date().toISOString(),
        createdBy: master.createdBy || userId,
        updatedDate: new Date().toISOString(),
        updatedById: userId,
        lines: validLines,
      }
      if (isEditMode) {
        await purchaseRequisitionService.update(payload)
      } else {
        await purchaseRequisitionService.save(payload)
      }
      await showSaveSuccess(isEditMode, t(isEditMode ? 'purchaseRequisitions.updatedSuccess' : 'purchaseRequisitions.savedSuccess'))
      navigate('/supply-chain/purchase-requisitions')
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.failed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <ClipboardList className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('purchaseRequisitions.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {isNew ? t('purchaseRequisitions.subtitleNew') : t('purchaseRequisitions.subtitleEdit')}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('purchaseRequisitions.orderDetails')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label>{t('purchaseRequisitions.fields.date')}</Label>
            <Input type="date" value={master.date} onChange={(e) => setMaster((p) => ({ ...p, date: e.target.value }))} disabled={saving || loading} />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseRequisitions.fields.reference')}</Label>
            <Input
              value={master.reference}
              onChange={(e) => setMaster((p) => ({ ...p, reference: e.target.value }))}
              placeholder={t('purchaseRequisitions.placeholders.reference')}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseRequisitions.fields.department')} *</Label>
            <SearchableSelect
              options={departmentOptions}
              value={String(master.departmentId || '')}
              onValueChange={(v) => setMaster((p) => ({ ...p, departmentId: Number(v || 0) }))}
              placeholder={t('purchaseRequisitions.placeholders.department')}
              searchPlaceholder={t('inventoryItems.searchPlaceholder')}
              emptyMessage={t('common.noData')}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseRequisitions.fields.estimatedDeliveryDate')}</Label>
            <Input
              type="date"
              value={master.estimatedDeliveryDate}
              onChange={(e) => setMaster((p) => ({ ...p, estimatedDeliveryDate: e.target.value }))}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseRequisitions.file')}</Label>
            <Input
              type="file"
              onChange={(e) => setMaster((p) => ({ ...p, attachmentFile: e.target.files?.[0] || null }))}
              disabled={saving || loading}
            />
            {master.attachmentPath ? (
              <p className="text-xs text-muted-foreground">
                {t('purchaseRequisitions.savedFile')}: {master.attachmentPath}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{t('purchaseRequisitions.noFileChosen')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('purchaseRequisitions.orderItems')}</CardTitle>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(1)}>
              <Plus className="mr-1 h-4 w-4" /> {t('purchaseRequisitions.addRow')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(3)}>
              {t('purchaseRequisitions.add3Rows')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(5)}>
              {t('purchaseRequisitions.add5Rows')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b bg-primary/5">
                <th className="p-2 text-left text-sm font-medium text-foreground">{t('purchaseRequisitions.item')}</th>
                <th className="p-2 text-left text-sm font-medium text-foreground">{t('purchaseRequisitions.quantity')}</th>
                <th className="p-2 w-[60px]"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={`${l.id}-${idx}`} className="border-b">
                  <td className="p-2">
                    <SearchableSelect
                      options={itemSelectOptions}
                      value={String(l.itemId || '')}
                      onValueChange={(v) => updateLine(idx, { itemId: Number(v || 0) })}
                      placeholder={t('inventoryDispatch.selectItem')}
                      searchPlaceholder={t('inventoryDispatch.searchPlaceholder')}
                      emptyMessage={t('common.noData')}
                      disabled={saving || loading}
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      min={0}
                      value={l.quantity || ''}
                      onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) || 0 })}
                      disabled={saving || loading}
                    />
                  </td>
                  <td className="p-2 text-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(idx)}
                      className="text-destructive hover:bg-destructive/10"
                      title={t('purchaseRequisitions.deleteRow')}
                      disabled={saving || loading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/supply-chain/purchase-requisitions')}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving || loading}>
          <Save className="mr-2 h-4 w-4" />
          {isEditMode ? t('common.update') : t('purchaseRequisitions.save')}
        </Button>
      </div>
    </div>
  )
}

